// v3E.3 — Lab reminders (recurring tests like HbA1c, lipid profile).
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, FlaskConical, RotateCcw, Trash2, CheckCircle2 } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";
import { useFamily } from "../contexts/FamilyContext";

const FREQ_OPTIONS = [
    { v: "monthly", label: "Monthly", days: 30 },
    { v: "quarterly", label: "Every 3 months", days: 90 },
    { v: "half_yearly", label: "Every 6 months", days: 180 },
    { v: "yearly", label: "Yearly", days: 365 },
    { v: "custom_days", label: "Custom (days)", days: null },
    { v: "one_time", label: "One time only", days: null },
];

const EMPTY_FORM = { test_name: "", frequency: "quarterly", custom_days: 90, last_done_date: "", notes: "" };

const LabReminderForm = ({ familyMemberId, onSubmit, onCancel, submitting }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    const submit = (e) => {
        e.preventDefault();
        const payload = {
            test_name: form.test_name.trim(),
            frequency: form.frequency,
            last_done_date: form.last_done_date || null,
            notes: form.notes || null,
            family_member_id: familyMemberId || null,
        };
        if (form.frequency === "custom_days") payload.custom_days = Number(form.custom_days) || 30;
        onSubmit(payload);
    };
    return (
        <form onSubmit={submit} className="space-y-3 rounded-3xl border border-brand-100 bg-brand-50/40 p-5 dark:border-brand-400/20 dark:bg-brand-500/5" data-testid="lab-rem-form">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Test name</span>
                    <input name="test_name" required value={form.test_name} onChange={onChange} placeholder="e.g. HbA1c, Lipid profile, Vitamin D" className="input-clean mt-1" data-testid="lab-rem-name" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">How often?</span>
                    <select name="frequency" value={form.frequency} onChange={onChange} className="input-clean mt-1" data-testid="lab-rem-freq">
                        {FREQ_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                </label>
                {form.frequency === "custom_days" && (
                    <label className="block">
                        <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Every N days</span>
                        <input name="custom_days" type="number" min="1" max="3650" value={form.custom_days} onChange={onChange} className="input-clean mt-1" data-testid="lab-rem-customdays" />
                    </label>
                )}
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Last done (optional)</span>
                    <input name="last_done_date" type="date" value={form.last_done_date} onChange={onChange} className="input-clean mt-1" data-testid="lab-rem-lastdone" />
                </label>
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Notes (optional)</span>
                    <input name="notes" value={form.notes} onChange={onChange} placeholder="e.g. fasting required, doctor's order date" className="input-clean mt-1" data-testid="lab-rem-notes" />
                </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onCancel} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5" data-testid="lab-rem-cancel">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-brand !py-2 !px-5 !text-sm" data-testid="lab-rem-save">{submitting ? "Saving..." : "Save reminder"}</button>
            </div>
        </form>
    );
};

const LabReminders = () => {
    const { activeMember } = useFamily();
    const fmId = activeMember && !activeMember.is_self ? activeMember.id : null;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [adding, setAdding] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (fmId) params.family_member_id = fmId;
            const { data } = await api.get("/lab-reminders", { params });
            setItems(data.items || []);
        } finally { setLoading(false); }
    }, [fmId]);

    useEffect(() => { load(); }, [load]);

    const onCreate = async (payload) => {
        try {
            setSubmitting(true);
            await api.post("/lab-reminders", payload);
            toast.success("Reminder added");
            setAdding(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        } finally { setSubmitting(false); }
    };

    const onMarkDone = async (it) => {
        try {
            const today = new Date().toISOString().slice(0, 10);
            await api.patch(`/lab-reminders/${it.id}`, { last_done_date: today });
            toast.success("Done — next due date updated");
            await load();
        } catch { toast.error("Could not update"); }
    };

    const onDelete = async (it) => {
        if (!window.confirm("Delete this reminder?")) return;
        try {
            await api.delete(`/lab-reminders/${it.id}`);
            toast.success("Reminder removed");
            await load();
        } catch { toast.error("Delete failed"); }
    };

    return (
        <Layout>
            <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
                <AuthGuard>
                    <header className="mb-6 flex flex-wrap items-end justify-between gap-3" data-testid="lab-reminders-page">
                        <div>
                            <p className="label-uppercase">Lab reminders</p>
                            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Recurring tests, never missed</h1>
                            <p className="mt-2 max-w-xl text-sm text-zinc-600 dark:text-zinc-400">
                                Add chronic tests (HbA1c, lipid profile, kidney function...) and we'll remind you to book them every 3 / 6 / 12 months.
                            </p>
                        </div>
                        {!adding && (
                            <button onClick={() => setAdding(true)} className="btn-brand !py-2 !px-4 !text-sm" data-testid="lab-rem-add-btn">
                                <Plus className="h-4 w-4" /> Add reminder
                            </button>
                        )}
                    </header>

                    {adding && (
                        <div className="mb-6">
                            <LabReminderForm familyMemberId={fmId} onSubmit={onCreate} onCancel={() => setAdding(false)} submitting={submitting} />
                        </div>
                    )}

                    {loading ? (
                        <div className="grid gap-3 md:grid-cols-2">
                            {[1, 2].map((i) => <div key={i} className="h-32 skeleton rounded-2xl" />)}
                        </div>
                    ) : items.length === 0 ? (
                        <EmptyState
                            title="No lab reminders yet"
                            description="Add one for HbA1c, lipid profile, vitamin D — anything recurring."
                            action={<button onClick={() => setAdding(true)} className="btn-brand"><Plus className="h-4 w-4" /> Add first reminder</button>}
                        />
                    ) : (
                        <div className="grid gap-3 md:grid-cols-2" data-testid="lab-rem-list">
                            {items.map((r) => (
                                <div key={r.id} data-testid={`lab-rem-card-${r.id}`} className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft transition hover:border-brand-200 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-brand-400/40">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start gap-3">
                                            <span className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
                                                <FlaskConical className="h-5 w-5" />
                                            </span>
                                            <div>
                                                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{r.test_name}</p>
                                                <p className="text-[11px] text-zinc-500 dark:text-zinc-400">Every {(FREQ_OPTIONS.find((o) => o.v === r.frequency) || {}).label || r.frequency}{r.frequency === "custom_days" ? ` (${r.custom_days} days)` : ""}</p>
                                                <p className="mt-1 text-xs text-zinc-700 dark:text-zinc-200">Next due: <span className="font-semibold">{r.next_due_date}</span></p>
                                                {r.last_done_date && <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">Last done: {r.last_done_date}</p>}
                                                {r.notes && <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{r.notes}</p>}
                                            </div>
                                        </div>
                                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${r.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-zinc-100 text-zinc-700 dark:bg-white/10 dark:text-zinc-300"}`}>{r.status}</span>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <button onClick={() => onMarkDone(r)} data-testid={`lab-rem-done-${r.id}`} className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:hover:bg-emerald-500/25">
                                            <CheckCircle2 className="mr-1 inline-block h-3 w-3" /> Mark done today
                                        </button>
                                        <button onClick={() => onDelete(r)} data-testid={`lab-rem-delete-${r.id}`} className="rounded-full border border-zinc-200 px-3 py-1 text-xs text-zinc-500 hover:border-rose-200 hover:text-rose-600 dark:border-white/10 dark:text-zinc-400 dark:hover:border-rose-400/30">
                                            <Trash2 className="mr-1 inline-block h-3 w-3" /> Delete
                                        </button>
                                        <a href="/lab-tests" className="inline-flex items-center gap-1 rounded-full border border-brand-200 px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-400/30 dark:text-brand-300 dark:hover:bg-brand-500/10">
                                            <RotateCcw className="h-3 w-3" /> Book now
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </AuthGuard>
            </section>
        </Layout>
    );
};

export default LabReminders;
